import { StateCreator } from 'zustand';

import { Commander, CommanderActions } from '@spazious/dollhouse-commander';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FSetupGraph } from '@spazious/dollhouse-fsetup';
import { History } from '@spazious/dollhouse-history';
import { FShallowGraph, GraphAction } from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';
import { World } from '@spazious/dollhouse-three';
import {
  ControlsModeEnum,
  MeasurementStackEnum,
  ScreenModeEnum,
  ScreenShadingEnum,
  ScreenTypeEnum,
  ThemeModeEnum,
  ToolModeEnum,
  UnitType,
  ViewModeEnum,
} from '@spazious/dollhouse-types';

import { FEATURES_TAGS } from './constants/index.js';
import { getSession } from './utils/index.js';
import { RootState } from './types.js';

const DEFAULT_LANGUAGE = window.navigator.language || 'en';

const getUserLocale = (): string => {
  try {
    return Intl.NumberFormat().resolvedOptions().locale || DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

const getDefaultMeasurementsUnit = (): UnitType => {
  const locale = getUserLocale().toUpperCase();
  const imperialRegions = ['-US', '-LR', '-MM'];

  return imperialRegions.some((region) => locale.endsWith(region)) ? UnitType.Feet : UnitType.Meter;
};

const shouldEnableCollisionsByDefault = (appName: string) => appName !== 'planner';

export const stateSlice =
  <
    TAction extends GraphAction = GraphAction,
    TMeta extends CommanderActionTypesEnum = CommanderActionTypesEnum
  >(
    appName: string,
    graph: FSetupGraph,
    staging: StagingArea<TAction, TMeta>,
    history: History<TAction[], TMeta>,
    world: World,
    commander: Commander<FShallowGraph, CommanderActions, TMeta>
  ): StateCreator<RootState<TAction, TMeta>, [], [], RootState<TAction, TMeta>> =>
  (set, get) => ({
    set,
    get,
    status: 'ready',
    selected: [],
    tick: 0,
    camera: {
      position: [0, 0, 0],
      azimuthAngle: 0,
      polarAngle: 0,
      fov: 45,
      enablePan: true,
    },
    view: ViewModeEnum.Normal,
    session: getSession(),
    theme: window.matchMedia('(prefers-color-scheme: dark)').matches
      ? ThemeModeEnum.Dark
      : ThemeModeEnum.Light,
    controls: ControlsModeEnum.Orbit,
    tool: ToolModeEnum.None,
    history,
    staging,
    commander,
    selection: [],
    features: FEATURES_TAGS,
    graph,
    world,
    screenMode: ScreenModeEnum.Single,
    screenRatio: 0.5,
    settings: {
      askBeforeDeleteNodes: true,
      askBeforeUnlinkNodes: true,
      ignoreRestrictedAreas: false,
      enableCollisions: shouldEnableCollisionsByDefault(appName),
      disableCameraTravelling: false,
      showButtonTitles: false,
      showGrid: false,
    },
    measurementsUnit: getDefaultMeasurementsUnit(),
    screens: {
      [ScreenTypeEnum.Primary]: {
        shading: ScreenShadingEnum.None,
        setShading: (shading) =>
          set((state) => ({
            ...state,
            shading: shading,
            screens: {
              ...state.screens,
              [ScreenTypeEnum.Primary]: {
                ...state.screens[ScreenTypeEnum.Primary],
                shading: shading,
              },
            },
          })),
      },
      [ScreenTypeEnum.Secondary]: {
        shading: ScreenShadingEnum.None,
        setShading: (shading) =>
          set((state) => ({
            ...state,
            shading: shading,
            screens: {
              ...state.screens,
              [ScreenTypeEnum.Secondary]: {
                ...state.screens[ScreenTypeEnum.Primary],
                shading: shading,
              },
            },
          })),
      },
    },
    language: DEFAULT_LANGUAGE,
    templatesCount: null,
    measurements: {
      drawEnabled: false,
      stack: MeasurementStackEnum.Initial,
    },
    showNavMesh: false,
    showNumbering: false,
    config: {
      rtsUrl: undefined,
    },
  });
